import React, { createContext, useContext, useState, useEffect } from 'react';
import { DATA_SOURCE_CONFIG } from '../config/appConfig';
import {
  loginApi,
  smsLoginApi,
  sendSmsVerificationApi,
  registerApi,
  logoutApi,
  getAuthToken,
  setAuthToken,
  setWeChatLoginSession
} from '../utils/authApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useServerAuth, setUseServerAuth] = useState(DATA_SOURCE_CONFIG.USE_SERVER_DATA);

  // 从localStorage加载用户信息和token
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash.includes('wechat_login=')) {
      const params = new URLSearchParams(window.location.hash.slice(1));
      const loginStatus = params.get('wechat_login');

      let cleanPath = window.location.pathname + window.location.search;

      if (loginStatus === 'success') {
        const authToken = params.get('token');
        const userPayload = params.get('user');
        const nextPath = params.get('next');

        if (authToken && userPayload) {
          try {
            const wechatUser = JSON.parse(userPayload);
            setWeChatLoginSession(authToken, wechatUser);
            setUser(wechatUser);
            setToken(authToken);
            if (nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//')) {
              cleanPath = nextPath;
            }
          } catch (error) {
            console.error('Error parsing WeChat login user:', error);
          }
        }
      } else if (loginStatus === 'error') {
        console.error('WeChat login failed:', params.get('message') || '未知错误');
      }

      window.history.replaceState(null, document.title, cleanPath);
    }

    const savedUser = localStorage.getItem('yogaUser');
    const savedToken = getAuthToken();

    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        if (savedToken) {
          setToken(savedToken);
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('yogaUser');
        setAuthToken(null);
      }
    }
    setLoading(false);
  }, []);

  // 保存用户信息到localStorage
  const saveUserToStorage = (userData) => {
    localStorage.setItem('yogaUser', JSON.stringify(userData));
  };

  // 发送验证码
  const sendVerificationCode = async (phoneNumber) => {
    if (useServerAuth) {
      return sendSmsVerificationApi(phoneNumber);
    }

    // 模拟API调用延迟
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // 生成6位随机验证码
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    
    // 在实际应用中，这里会调用短信服务API
    console.log(`验证码已发送到 ${phoneNumber}: ${code}`);
    
    // 将验证码保存到localStorage（仅用于演示）
    localStorage.setItem(`verificationCode_${phoneNumber}`, code);
    localStorage.setItem(`verificationCodeTime_${phoneNumber}`, Date.now().toString());
    
    return { success: true, message: '验证码已发送' };
  };

  // 验证验证码
  const verifyCode = (phoneNumber, inputCode) => {
    const savedCode = localStorage.getItem(`verificationCode_${phoneNumber}`);
    const savedTime = localStorage.getItem(`verificationCodeTime_${phoneNumber}`);
    
    if (!savedCode || !savedTime) {
      return { success: false, message: '请先获取验证码' };
    }
    
    // 检查验证码是否过期（5分钟）
    const now = Date.now();
    const codeTime = parseInt(savedTime);
    if (now - codeTime > 5 * 60 * 1000) {
      localStorage.removeItem(`verificationCode_${phoneNumber}`);
      localStorage.removeItem(`verificationCodeTime_${phoneNumber}`);
      return { success: false, message: '验证码已过期，请重新获取' };
    }
    
    if (savedCode === inputCode) {
      // 验证成功后清除验证码
      localStorage.removeItem(`verificationCode_${phoneNumber}`);
      localStorage.removeItem(`verificationCodeTime_${phoneNumber}`);
      return { success: true, message: '验证码验证成功' };
    } else {
      return { success: false, message: '验证码错误' };
    }
  };

  // 检查手机号是否已存在
  const checkPhoneExists = (phoneNumber) => {
    const users = JSON.parse(localStorage.getItem('yogaUsers') || '[]');
    return users.some(user => user.phoneNumber === phoneNumber);
  };

  // 检查用户名是否已存在
  const checkUsernameExists = (username) => {
    const users = JSON.parse(localStorage.getItem('yogaUsers') || '[]');
    return users.some(user => user.username === username);
  };

  // 注册用户
  const register = async (userData) => {
    const { name, phoneNumber, email, password, verificationCode } = userData;

    // 如果使用服务器认证，直接调用API
    if (useServerAuth) {
      const result = await registerApi({
        name,
        username: userData.username || name,
        email,
        phoneNumber,
        verificationCode,
        password,
        confirmPassword: userData.confirmPassword || password,
      });

      if (result.success) {
        setUser(result.user);
        setToken(result.token);
        saveUserToStorage(result.user);
      }
      return result;
    }

    // 本地模式：验证验证码
    const codeVerification = verifyCode(phoneNumber, verificationCode);
    if (!codeVerification.success) {
      return codeVerification;
    }

    // 检查手机号是否已存在
    if (checkPhoneExists(phoneNumber)) {
      return { success: false, message: '该手机号已被注册' };
    }

    // 检查用户名是否已存在
    if (checkUsernameExists(name)) {
      return { success: false, message: '该姓名已被使用' };
    }

    // 创建新用户
    const newUser = {
      id: Date.now().toString(),
      username: name,
      name,
      phoneNumber,
      email,
      password, // 在实际应用中应该加密
      createdAt: new Date().toISOString(),
      avatar: null
    };

    // 保存到用户列表
    const users = JSON.parse(localStorage.getItem('yogaUsers') || '[]');
    users.push(newUser);
    localStorage.setItem('yogaUsers', JSON.stringify(users));

    // 自动登录
    const userForAuth = { ...newUser };
    delete userForAuth.password; // 不在认证状态中保存密码

    setUser(userForAuth);
    saveUserToStorage(userForAuth);

    return { success: true, message: '注册成功' };
  };

  // 登录 - 支持用户名或手机号
  const login = async (usernameOrPhone, password) => {
    // 如果使用服务器认证，调用API
    if (useServerAuth) {
      const result = await loginApi(usernameOrPhone, password);

      if (result.success) {
        setUser(result.user);
        setToken(result.token);
        saveUserToStorage(result.user);
      }
      return result;
    }

    // 本地模式登录
    const users = JSON.parse(localStorage.getItem('yogaUsers') || '[]');
    const user = users.find(u =>
      (u.phoneNumber === usernameOrPhone || u.username === usernameOrPhone) &&
      u.password === password
    );

    if (user) {
      const userForAuth = { ...user };
      delete userForAuth.password; // 不在认证状态中保存密码

      setUser(userForAuth);
      saveUserToStorage(userForAuth);

      return { success: true, message: '登录成功' };
    } else {
      return { success: false, message: '用户名/手机号或密码错误' };
    }
  };

  const smsLogin = async ({ phoneNumber, verificationCode, username }) => {
    if (useServerAuth) {
      const result = await smsLoginApi({ phoneNumber, verificationCode, username });

      if (result.success) {
        setUser(result.user);
        setToken(result.token);
        saveUserToStorage(result.user);
      }
      return result;
    }

    const codeVerification = verifyCode(phoneNumber, verificationCode);
    if (!codeVerification.success) {
      return codeVerification;
    }

    const users = JSON.parse(localStorage.getItem('yogaUsers') || '[]');
    const existingUser = users.find(u => u.phoneNumber === phoneNumber);

    if (existingUser) {
      const userForAuth = { ...existingUser };
      delete userForAuth.password;
      setUser(userForAuth);
      saveUserToStorage(userForAuth);
      return { success: true, message: '登录成功', isNewUser: false };
    }

    const displayName = (username || '').trim();
    if (!displayName) {
      return { success: false, needsUsername: true, message: '请先设置用户名' };
    }
    if (checkUsernameExists(displayName)) {
      return { success: false, message: '该用户名已被使用' };
    }

    const newUser = {
      id: Date.now().toString(),
      username: displayName,
      name: displayName,
      phoneNumber,
      email: '',
      createdAt: new Date().toISOString(),
      avatar: null
    };
    const nextUsers = [...users, newUser];
    localStorage.setItem('yogaUsers', JSON.stringify(nextUsers));
    setUser(newUser);
    saveUserToStorage(newUser);
    return { success: true, message: '账号已创建', isNewUser: true };
  };

  // 登出
  const logout = async () => {
    if (useServerAuth) {
      await logoutApi();
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('yogaUser');
    setAuthToken(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    smsLogin,
    register,
    logout,
    sendVerificationCode,
    checkPhoneExists,
    checkUsernameExists,
    useServerAuth,
    setUseServerAuth,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
